import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import Security from '@mui/icons-material/Security'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import GroupIcon from '@mui/icons-material/Group'
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined'
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined'
import { Box, Button, Stack, useMediaQuery, useTheme } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useNavigate } from 'react-router'
import useServerAuth from 'src/customHooks/useServerAuth'
import { MENTOR_MENU_ITEMS } from 'src/supportPortal/components/common/mentor/listing/utils'

const PAYOUT_WORKSPACE_MENU_ITEM_IDS = new Set([
  MENTOR_MENU_ITEMS.ENGAGEMENT_MODELS.id,
  MENTOR_MENU_ITEMS.PAYOUT_REPORTS.id,
  MENTOR_MENU_ITEMS.PAYOUT_RECEIPTS.id,
])

const StyledButton = styled(Button)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 500,
  textTransform: 'none',
  borderRadius: '8px',
  padding: theme.spacing(0.5, 1.25),
  minWidth: 'auto',
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.6875rem',
    padding: theme.spacing(0.4, 1),
    flex: '1 1 auto',
    minWidth: '80px',
  },
}))

const ActionButtonsContainer = styled(Stack)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  alignItems: 'center',
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(0.75),
    width: '100%',
  },
}))

/**
 * The full set of per-practitioner action launchers (Roster / Appointments / Events / EMR
 * Settings / Patients / …) — extracted from SupportMentorsListing so the practitioner detail
 * page can surface the exact same actions in the same context (therapist / doctor / screener).
 *
 * `variant` drives the base path and role-scoped buttons; `excludeMenuItemsList` hides menu
 * items the caller doesn't want (the detail page passes 'clinics' since it IS the clinics
 * page). Fee-model-gated buttons (Engagement Models / Payout Reports) only disable when the
 * practitioner object carries an explicit empty `entityFeeModels` array.
 */
export default function PractitionerActionButtons({
  practitioner,
  variant = 'therapist',
  excludeMenuItemsList = [],
}) {
  const navigate = useNavigate()
  const { allRolesInBoolean } = useServerAuth()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const isDoctors = variant === 'doctor'
  const isScreener = variant === 'screener'
  const targetRole = isScreener ? 'screener' : isDoctors ? 'doctor' : 'therapist'
  const basePath = isDoctors
    ? '/support-home/practitioner-management/doctors'
    : isScreener
      ? '/support-home/practitioner-management/screeners'
      : '/support-home/practitioner-management/therapists'

  const canViewTherapistPayoutReports =
    !isDoctors && !isScreener && allRolesInBoolean.isTherapistAdmin
  // Screener "Payouts" workspace: opt-in per screener via ScreenerProfile.payoutsEnabled, and
  // gated by the same role as therapist payouts (ROLE_THERAPIST_ADMIN). When this is false the
  // screener shows no engagement-models / payout-receipts / payout-reports buttons at all — they
  // only ever appear consolidated inside this Payouts workspace.
  const canViewScreenerPayouts =
    isScreener &&
    Boolean(practitioner?.screenerPayoutsEnabled) &&
    allRolesInBoolean.isTherapistAdmin
  const hasPrescriptionTemplatesAccess =
    isDoctors &&
    (allRolesInBoolean.isClinicSuccessAdmin || allRolesInBoolean.isClinicSuccessManager)
  const canCommunicateWithTherapists =
    targetRole === 'therapist' &&
    (allRolesInBoolean.isAdmin || allRolesInBoolean.isTherapistAdmin)
  const canOpenAuditHub =
    allRolesInBoolean.isAdmin ||
    allRolesInBoolean.isAuditAdmin ||
    (targetRole === 'therapist' && allRolesInBoolean.isTherapistAuditAdmin) ||
    (targetRole === 'screener' && allRolesInBoolean.isScreenerAuditAdmin)
  // Therapist Admin (or Admin) can assign/edit/revoke the practitioner auditor roles +
  // profile. The "Auditing Settings" button shows for every practitioner so a not-yet-auditor
  // can be made one; users who can only open the audit hub keep the navigate-to-hub behaviour.
  const canManageAuditorRoles = allRolesInBoolean.isAdmin || allRolesInBoolean.isTherapistAdmin
  const showAuditingSettings = canManageAuditorRoles || canOpenAuditHub
  // The auditor role relevant to this practitioner's base role (doctors aren't auditors).
  const relevantAuditorRole =
    targetRole === 'screener'
      ? 'ROLE_SCREENING_AUDITOR'
      : targetRole === 'therapist'
        ? 'ROLE_THERAPY_AUDITOR'
        : null

  // Only treat fee-model-gated buttons as disabled when we explicitly know there are no
  // models (listing rows attach the array; the detail page leaves it undefined → enabled).
  const hasNoFeeModels =
    Array.isArray(practitioner?.entityFeeModels) && practitioner.entityFeeModels.length === 0

  function go(suffix) {
    navigate(`${basePath}/${practitioner?.slug}${suffix}`)
    sessionStorage.setItem('supportMentorData', JSON.stringify(practitioner))
  }

  function goToAuditHub({ assign } = {}) {
    const params = new URLSearchParams()
    // Managers land straight on the "Assign Auditor Role" section scoped to the relevant
    // role; everyone else lands on the audit-action tiles (existing behaviour).
    if (assign && relevantAuditorRole) {
      params.set('section', 'assign-auditor-role')
      params.set('role', relevantAuditorRole)
    }
    const base = '/support-home/practitioner-management/auditors/audit'
    // Human-readable slug in the path; no redundant ids.
    if (practitioner?.slug) {
      const query = params.toString()
      navigate(`${base}/${encodeURIComponent(practitioner.slug)}${query ? `?${query}` : ''}`)
      return
    }
    if (practitioner?.id) {
      params.set('practitionerId', practitioner.id)
    }
    const fallbackQuery = params.toString()
    navigate(`${base}${fallbackQuery ? `?${fallbackQuery}` : ''}`)
  }

  function renderMenuItem(itemId) {
    switch (itemId) {
      case MENTOR_MENU_ITEMS.ROSTER.id:
        return (
          <StyledButton
            variant="contained"
            size="small"
            startIcon={<CalendarMonthIcon sx={{ fontSize: '14px' }} />}
            onClick={() => go('/roster')}
            sx={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              color: '#fff',
              boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5558e3 0%, #7c4ee8 100%)',
                boxShadow: '0 4px 12px rgba(99, 102, 241, 0.4)',
              },
            }}
          >
            Roster
          </StyledButton>
        )
      case MENTOR_MENU_ITEMS.CLINICS.id:
        return (
          <StyledButton variant="outlined" size="small" onClick={() => go('')}>
            Clinics
          </StyledButton>
        )
      case MENTOR_MENU_ITEMS.PAYOUT_RECEIPTS.id:
        return (
          <StyledButton variant="outlined" size="small" onClick={() => go('/payout-receipts')}>
            Payout Receipts
          </StyledButton>
        )
      case MENTOR_MENU_ITEMS.CALENDAR.id:
        return (
          <StyledButton variant="outlined" size="small" onClick={() => go('/calendar')}>
            Calendar
          </StyledButton>
        )
      case MENTOR_MENU_ITEMS.EVENTS.id:
        return (
          <StyledButton variant="outlined" size="small" onClick={() => go('/events?tab=events')}>
            Events
          </StyledButton>
        )
      case MENTOR_MENU_ITEMS.APPOINTMENTS.id:
        return (
          <StyledButton variant="outlined" size="small" onClick={() => go('/appointments')}>
            Appointments
          </StyledButton>
        )
      case MENTOR_MENU_ITEMS.ENGAGEMENT_MODELS.id:
        return (
          <StyledButton
            variant="outlined"
            size="small"
            disabled={hasNoFeeModels}
            startIcon={
              practitioner?.teacherUnEditableFields?.showFeeModels ? (
                <Visibility sx={{ fontSize: '14px' }} />
              ) : (
                <VisibilityOff sx={{ fontSize: '14px' }} />
              )
            }
            color={practitioner?.teacherUnEditableFields?.showFeeModels ? 'success' : 'primary'}
            onClick={() => go('/engagement-models')}
          >
            {!isMobile && 'Engagement Models'}
            {isMobile && 'Engagement'}
          </StyledButton>
        )
      case MENTOR_MENU_ITEMS.PAYOUT_REPORTS.id:
        if (!canViewTherapistPayoutReports) {
          return null
        }
        return (
          <StyledButton
            variant="outlined"
            size="small"
            disabled={hasNoFeeModels}
            startIcon={
              practitioner?.teacherUnEditableFields?.showPayoutReports ? (
                <Visibility sx={{ fontSize: '14px' }} />
              ) : (
                <VisibilityOff sx={{ fontSize: '14px' }} />
              )
            }
            onClick={() => go('/payout-reports')}
            color={
              practitioner?.teacherUnEditableFields?.showPayoutReports ? 'success' : 'primary'
            }
          >
            {!isMobile && 'Payout Reports'}
            {isMobile && 'Reports'}
          </StyledButton>
        )
      case MENTOR_MENU_ITEMS.NEXT_VISITS.id:
        // Not relevant to screeners — hidden there (stays on therapists / doctors).
        if (isScreener) {
          return null
        }
        return (
          <StyledButton variant="outlined" size="small" onClick={() => go('/next-visits')} disabled>
            {!isMobile && 'Scheduled Visits'}
            {isMobile && 'Visits'}
          </StyledButton>
        )
      case MENTOR_MENU_ITEMS.EMR_SETTINGS.id:
        return (
          <StyledButton variant="outlined" size="small" onClick={() => go('/emr-settings')}>
            {!isMobile && 'EMR Settings'}
            {isMobile && 'EMR'}
          </StyledButton>
        )
      case MENTOR_MENU_ITEMS.PRESCRIPTION_TEMPLATES.id:
        if (!hasPrescriptionTemplatesAccess) {
          return null
        }
        return (
          <StyledButton
            variant="outlined"
            size="small"
            onClick={() => go('/prescription-templates')}
          >
            {!isMobile && 'Prescription Templates'}
            {isMobile && 'Templates'}
          </StyledButton>
        )
      case MENTOR_MENU_ITEMS.INTERVENTIONS.id:
        if (
          !isDoctors ||
          (!allRolesInBoolean.isClinicSuccessAdmin && !allRolesInBoolean.isClinicSuccessManager)
        ) {
          return null
        }
        return (
          <StyledButton
            variant="outlined"
            size="small"
            onClick={() =>
              navigate(
                `/support-home/prescription-interventions?triggeringDoctorId=${practitioner?.id}&triggeringDoctorSlug=${practitioner?.slug}`,
              )
            }
          >
            {!isMobile && 'Interventions Requested'}
            {isMobile && 'Interventions'}
          </StyledButton>
        )
      case MENTOR_MENU_ITEMS.AUDIT_REQUESTS.id:
        if (!showAuditingSettings) {
          return null
        }
        return (
          <StyledButton
            variant="outlined"
            size="small"
            startIcon={<Security sx={{ fontSize: '14px' }} />}
            onClick={() =>
              goToAuditHub({ assign: canManageAuditorRoles && Boolean(relevantAuditorRole) })
            }
            sx={{
              borderColor: '#8b5cf6',
              color: '#8b5cf6',
              '&:hover': {
                borderColor: '#7c3aed',
                backgroundColor: 'rgba(139, 92, 246, 0.08)',
              },
            }}
          >
            {!isMobile && 'Auditing Settings'}
            {isMobile && 'Auditing'}
          </StyledButton>
        )
      case MENTOR_MENU_ITEMS.PATIENTS.id:
        return (
          <StyledButton
            variant="contained"
            size="small"
            startIcon={<GroupIcon sx={{ fontSize: '14px' }} />}
            onClick={() => go('/patients')}
            sx={{
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: '#fff',
              boxShadow: '0 2px 8px rgba(240, 147, 251, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #f5576c 0%, #f093fb 100%)',
                boxShadow: '0 4px 12px rgba(240, 147, 251, 0.4)',
              },
            }}
          >
            Patients
          </StyledButton>
        )
      default:
        return null
    }
  }

  if (!practitioner?.slug) {
    return null
  }

  // ROSTER first, APPOINTMENTS second, then the rest — mirrors the listing row ordering.
  const menuItemsEntries = Object.entries(MENTOR_MENU_ITEMS)
  const rosterEntry = menuItemsEntries.find(([, val]) => val?.id === MENTOR_MENU_ITEMS.ROSTER.id)
  const appointmentsEntry = menuItemsEntries.find(
    ([, val]) => val?.id === MENTOR_MENU_ITEMS.APPOINTMENTS.id,
  )
  const otherEntries = menuItemsEntries.filter(
    ([, val]) =>
      val?.id !== MENTOR_MENU_ITEMS.ROSTER.id && val?.id !== MENTOR_MENU_ITEMS.APPOINTMENTS.id,
  )
  const orderedEntries = [
    ...(rosterEntry ? [rosterEntry] : []),
    ...(appointmentsEntry ? [appointmentsEntry] : []),
    ...otherEntries,
  ]

  return (
    <ActionButtonsContainer>
      {orderedEntries
        .filter(([, val]) => {
          if (excludeMenuItemsList.includes(val?.id)) {
            return false
          }
          // Therapists and screeners consolidate engagement-models / payout-receipts /
          // payout-reports into a single "Payouts" workspace button (rendered below), so keep
          // them out of the main row. Doctors keep them inline.
          const consolidatesPayouts = targetRole === 'therapist' || targetRole === 'screener'
          return !(consolidatesPayouts && PAYOUT_WORKSPACE_MENU_ITEM_IDS.has(val?.id))
        })
        .map(([key, val]) => (
          <Box key={val?.id || key}>{renderMenuItem(val?.id)}</Box>
        ))}
      {/* Bundle Packs — not relevant to screeners; hidden there, kept for therapists / doctors. */}
      {!isScreener && (
        <Box>
          <StyledButton
            variant="outlined"
            size="small"
            onClick={() => go('/events?tab=packs')}
          >
            Bundle Packs
          </StyledButton>
        </Box>
      )}
      {targetRole === 'therapist' && canViewTherapistPayoutReports && (
        <Box>
          <StyledButton
            variant="outlined"
            size="small"
            startIcon={<AccountBalanceWalletOutlinedIcon sx={{ fontSize: '14px' }} />}
            onClick={() => go('/payouts')}
          >
            Payouts
          </StyledButton>
        </Box>
      )}
      {targetRole === 'screener' && canViewScreenerPayouts && (
        <Box>
          <StyledButton
            variant="outlined"
            size="small"
            startIcon={<AccountBalanceWalletOutlinedIcon sx={{ fontSize: '14px' }} />}
            onClick={() => go('/payouts')}
          >
            Payouts
          </StyledButton>
        </Box>
      )}
      {canCommunicateWithTherapists && (
        <Box>
          <StyledButton
            variant="outlined"
            size="small"
            startIcon={<CampaignOutlinedIcon sx={{ fontSize: '14px' }} />}
            onClick={() => go('/communications')}
          >
            Communicate
          </StyledButton>
        </Box>
      )}
    </ActionButtonsContainer>
  )
}
